// src/experiments/overrides.js
//
// QA overrides. `?exp=key:variant` (comma-separated for several) forces an arm
// for this browser tab and is remembered in sessionStorage so it survives the
// navigation that drops the query string. `?exp=clear` forgets it. A
// `window.__hippoForceExperiment` value set from devtools — `{ key: variant }`
// or `'key:variant'` — wins over both. Overrides work on draft experiments too,
// so a variant can be previewed before it is started.
//
// The app's `sessionStorage.clear()` calls (logout, "no, that's not me", the
// reset shortcut) also drop the override. That is acceptable for QA.

const PARAM = 'exp';
const SESSION_KEY = 'hippo_exp_override';
const TOKEN = /^[a-z0-9_-]+$/i;

export function parseOverrideValue(raw) {
  const result = {};
  if (typeof raw !== 'string') return result;
  for (const entry of raw.split(',')) {
    const [key, variant] = entry.split(':').map((s) => s.trim());
    if (key && variant && TOKEN.test(key) && TOKEN.test(variant)) {
      result[key] = variant;
    }
  }
  return result;
}

function readSession() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeSession(value) {
  try {
    if (value && Object.keys(value).length) {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(value));
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  } catch {
    // storage blocked
  }
}

// Reads `?exp=` out of a search string and persists it. Returns the stored
// overrides after the update. Safe to call on every read: with no `exp`
// parameter present it changes nothing.
export function captureOverridesFromSearch(search) {
  let params;
  try {
    params = new URLSearchParams(search || '');
  } catch {
    return readSession();
  }
  if (!params.has(PARAM)) return readSession();

  const raw = params.get(PARAM);
  if (raw === '' || raw === 'clear') {
    writeSession(null);
    return {};
  }
  const merged = { ...readSession(), ...parseOverrideValue(raw) };
  writeSession(merged);
  return merged;
}

function readWindowForce() {
  try {
    const force =
      typeof window !== 'undefined' ? window.__hippoForceExperiment : null;
    if (!force) return {};
    if (typeof force === 'string') return parseOverrideValue(force);
    if (typeof force === 'object') {
      const out = {};
      for (const [key, variant] of Object.entries(force)) {
        if (
          TOKEN.test(key) &&
          typeof variant === 'string' &&
          TOKEN.test(variant)
        ) {
          out[key] = variant;
        }
      }
      return out;
    }
  } catch {
    // ignore a malformed override
  }
  return {};
}

export function getOverrides() {
  const fromSession =
    typeof window !== 'undefined'
      ? captureOverridesFromSearch(window.location.search)
      : {};
  return { ...fromSession, ...readWindowForce() };
}

export const OVERRIDE_SESSION_KEY = SESSION_KEY;
